/**
 * LoginForm Component
 * Handles user login with validation
 * 
 * Requirements: 2.1, 2.2
 */

import { useState } from 'react';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const LoginForm = ({ onSuccess, onRequire2FA }) => {
  const { login } = useAuth();
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  
  // UI state
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Validate email format
   * Requirement: 2.2 - Form validation
   */
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  /**
   * Validate entire form
   * Returns validation errors object
   */
  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.email.trim()) {
      newErrors.email = "L'email est requis";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "L'email n'est pas valide";
    }
    if (!formData.password) {
      newErrors.password = 'Le mot de passe est requis';
    }

    return newErrors;
  };

  /**
   * Handle input change
   */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  /**
   * Handle form submission
   * Requirement: 2.1 - User login
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      // Call login method from AuthContext
      const response = await login(formData.email, formData.password);
      
      // Check if 2FA is required
      if (response.requires_2fa) {
        // Notify parent component to show 2FA modal
        if (onRequire2FA) {
          onRequire2FA(formData.email);
        }
        return;
      }
      
      // Requirement: 2.4 - Redirect to dashboard after successful login
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      // Handle API errors
      // Requirement: 2.2 - Display error for invalid credentials
      if (error.response?.data?.message) {
        setErrors({ general: error.response.data.message });
      } else if (error.response?.status === 401) {
        setErrors({ general: 'Email ou mot de passe incorrect' });
      } else {
        setErrors({ general: "Une erreur s'est produite lors de la connexion" });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* General Error */}
      {errors.general && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
          {errors.general}
        </div>
      )}

      {/* Email Field */}
      <div>
        <div className="relative group">
          <div className="absolute left-4 top-3.5 text-gray-400 group-focus-within:text-[#17386E] transition-colors">
            <Mail size={18}/>
          </div>
          <input 
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Adresse email"
            className={`w-full bg-[#F5F5F5] border rounded-xl py-3 pl-12 pr-4 font-montserrat text-sm text-[#4A4A4A] placeholder-gray-400 focus:bg-white focus:border-[#17386E] focus:outline-none focus:ring-2 focus:ring-[#EAF4FB] transition-all ${
              errors.email ? 'border-red-500' : 'border-transparent'
            }`}
          />
        </div>
        {errors.email && (
          <p className="mt-1 text-xs text-red-600">{errors.email}</p>
        )}
      </div>

      {/* Password Field */}
      <div>
        <div className="relative group">
          <div className="absolute left-4 top-3.5 text-gray-400 group-focus-within:text-[#17386E] transition-colors">
            <Lock size={18}/>
          </div>
          <input 
            type={showPassword ? "text" : "password"}
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="Mot de passe"
            className={`w-full bg-[#F5F5F5] border rounded-xl py-3 pl-12 pr-12 font-montserrat text-sm text-[#4A4A4A] placeholder-gray-400 focus:bg-white focus:border-[#17386E] focus:outline-none focus:ring-2 focus:ring-[#EAF4FB] transition-all ${
              errors.password ? 'border-red-500' : 'border-transparent'
            }`}
          />
          <button 
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-4 top-3.5 text-gray-400 hover:text-[#17386E]"
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        {errors.password && (
          <p className="mt-1 text-xs text-red-600">{errors.password}</p>
        )}
      </div>

      {/* Remember Me Checkbox (Optional) */}
      <div className="flex items-center">
        <input 
          type="checkbox"
          id="rememberMe"
          name="rememberMe"
          checked={formData.rememberMe}
          onChange={handleChange}
          className="w-4 h-4 text-[#17386E] bg-gray-100 border-gray-300 rounded focus:ring-[#17386E] focus:ring-2"
        />
        <label 
          htmlFor="rememberMe" 
          className="ml-2 text-sm font-montserrat text-gray-600 cursor-pointer"
        >
          Se souvenir de moi
        </label>
      </div>

      {/* Submit Button */}
      <button 
        type="submit"
        disabled={isSubmitting}
        className="btn-primary w-full py-4 rounded-xl font-playfair font-bold text-lg shadow-lg mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSubmitting ? 'Connexion en cours...' : 'Se connecter'}
      </button>
    </form>
  );
};

export default LoginForm;
